import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameCard, GameHero, GameIcon, Skeleton } from "../../components/game";
import { resolveMediaUrl } from "../student/quiz/media";
import api from "../../services/api";

type ClubProfile = {
  id: string;
  name: string;
  city: string | null;
  county: string | null;
  description: string | null;
  logo_url: string | null;
  website: string | null;
  counts: { teams: number; athletes: number };
  teams: { id: string; name: string; sport: string | null; age_group: string | null }[];
};

export default function AppClubProfile() {
  const { t } = useTranslation();
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const [club, setClub] = useState<ClubProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clubId) return;
    setLoading(true);
    api
      .get<ClubProfile>(`/profiles/club/${clubId}`)
      .then((r) => setClub(r.data))
      .catch((e) => setError(e?.response?.data?.error || t("club.unavailable", "Club indisponibil")))
      .finally(() => setLoading(false));
  }, [clubId]);

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-5">
        <GameHero flow className="-mx-4 -mt-6 rounded-br-3xl px-4 pt-3 pb-5">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-white/80 text-sm font-semibold mb-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            {t("club.back", "Înapoi")}
          </button>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white/15 grid place-items-center overflow-hidden shrink-0 ring-2 ring-white/20">
              {club?.logo_url ? (
                <img src={resolveMediaUrl(club.logo_url) || undefined} alt="" className="w-full h-full object-cover" />
              ) : (
                <GameIcon name="trophy" className="w-7 h-7 text-white/90" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-white/70 text-[11px] font-bold uppercase tracking-wide">{t("club.clubLabel", "Club")}</p>
              <h1 className="text-xl font-extrabold truncate">{club?.name || (loading ? "…" : t("club.clubLabel", "Club"))}</h1>
              {club?.city && <p className="text-white/70 text-[13px]">{club.city}{club.county ? `, ${club.county}` : ""}</p>}
            </div>
          </div>
        </GameHero>

        {loading ? (
          <div className="space-y-3 animate-fadeIn">
            <Skeleton className="h-20 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
          </div>
        ) : error || !club ? (
          <GameCard className="p-8 text-center text-slate-500">{error || t("club.loadError", "Nu s-au putut încărca datele")}</GameCard>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <GameCard className="p-4 text-center">
                <p className="text-2xl font-extrabold text-indigo-600 tabular-nums">{club.counts.teams}</p>
                <p className="text-xs text-slate-500 mt-0.5">{club.counts.teams === 1 ? t("club.teamOne", "Echipă") : t("club.teamMany", "Echipe")}</p>
              </GameCard>
              <GameCard className="p-4 text-center">
                <p className="text-2xl font-extrabold text-emerald-600 tabular-nums">{club.counts.athletes}</p>
                <p className="text-xs text-slate-500 mt-0.5">{club.counts.athletes === 1 ? t("club.athleteOne", "Sportiv") : t("club.athleteMany", "Sportivi")}</p>
              </GameCard>
            </div>

            {club.description && (
              <GameCard className="p-4">
                <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{club.description}</p>
              </GameCard>
            )}

            {club.teams.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-slate-700 mb-2">{t("club.teamsHeading", "Echipe")}</h2>
                <div className="space-y-2.5">
                  {club.teams.map((tm) => (
                    <GameCard
                      key={tm.id}
                      onClick={() => navigate(`/app/team/${tm.id}`)}
                      className="p-4 flex items-center gap-3 cursor-pointer active:translate-y-[1px] transition"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 grid place-items-center text-white shrink-0">
                        <GameIcon name="medal" className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-800 truncate">{tm.name}</p>
                        <p className="text-xs text-slate-500">
                          {[tm.sport, tm.age_group].filter(Boolean).join(" · ") || t("club.teamOne", "Echipă")}
                        </p>
                      </div>
                      <svg className="w-5 h-5 text-slate-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </GameCard>
                  ))}
                </div>
              </div>
            )}

            {club.website && (
              <a
                href={club.website.startsWith("http") ? club.website : `https://${club.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-sm font-semibold text-indigo-600"
              >
                {club.website}
              </a>
            )}
          </>
        )}
      </div>
    </MobileAppLayout>
  );
}
